// Angular
import { NgModule } from '@angular/core';
import { Routes, RouterModule, Router, NavigationEnd, NavigationStart } from '@angular/router';
// Shared module
import { LoginComponent } from './shared/login/login.component';
import { PageNotFoundComponent } from './shared/navigation/page-not-found.component';
import { ChangePasswordComponent } from './shared/changePassword/change-password.component';
// Core module
import { AuthGuard } from './core/user/auth-guard.service';
import { AccessDeniedComponent } from './shared/login/access-denied.component';
import { CanDeactivateGuard } from './shared/can-deactivate-route-guard/can-deactivate-guard.service';
import { UserService, ChartFilterService } from './core';

type PathMatch = "full" | "prefix" | undefined;
/// Define top-level routes.
export const routes: Routes = [

    {
        path: '',
        redirectTo: 'home/dashboard',
        pathMatch: 'full' as PathMatch,
    },
    {
        path: 'login',
        component: LoginComponent,
        data: {title: 'MyRemedi Login'}
    },
    {
        path: 'access-denied',
        component: AccessDeniedComponent
    },
    {
        path: 'changePassword',
        component: ChangePasswordComponent,
        canDeactivate:[CanDeactivateGuard]
    },
    // Lazy load modules
    {
        path: 'home',
        loadChildren: () => import('../app/home/home.module').then(mod => mod.HomeModule),
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard]
    },
    {
        path: 'alerts',
        loadChildren: () => import('../app/home/med-alerts/med-alerts.module').then(mod => mod.MedAlertsModule),
        canLoad: [AuthGuard],
        canActivateChild: [AuthGuard],
        data: {roles: ['Corporate', 'DON', 'Nurse', 'BusinessOffice']}
    },
    {
        path: 'residents',
        loadChildren: () => import('../app/residents/residents.module').then(mod => mod.ResidentsModule),
        canLoad: [AuthGuard],
        canActivateChild: [AuthGuard],
        data: {roles: ['Corporate', 'DON', 'Nurse']}
    },
    {
        path: 'reports',
        loadChildren: () => import('../app/reports/reports.module').then(mod => mod.ReportsModule),
        canLoad: [AuthGuard],
        canActivateChild: [AuthGuard],
        data: {roles: ['Corporate', 'DON', 'Nurse', 'BusinessOffice']}
    },
    {
        path: 'search',
        loadChildren: () => import('../app/search/search.module').then(mod => mod.SearchModule),
        canLoad: [AuthGuard],
        canActivateChild: [AuthGuard],
        data: {roles: ['Corporate', 'DON', 'Nurse', 'BusinessOffice']}
    },
    {
        path: 'resources',
        loadChildren: () => import('../app/resources/resources.module').then(mod => mod.ResourcesModule),
        canActivate:[AuthGuard],
        canActivateChild: [AuthGuard]
    },
    {
        path: 'profile',
        loadChildren: () => import('../app/profile-pages/profile-pages.module').then(mod => mod.ProfilePagesModule),
        canLoad: [AuthGuard],
        canActivateChild: [AuthGuard]
    },
    {
        path: 'admin-tools',
        loadChildren: () => import('../app/admin-tools/admin-tools.module').then(mod => mod.AdminToolsModule),
        canLoad: [AuthGuard],
        canActivateChild: [AuthGuard],
        data: {roles:['UserAdministration']}
    },
    {
        path: '**',
        component: PageNotFoundComponent
    },

];

/// Define the routing module
@NgModule({
    imports: [RouterModule.forRoot(routes, {useHash: true, enableTracing: false})],
    exports: [RouterModule]
})

export class AppRoutingModule {
    oldPath: string = '';
    path: string = '/reports/summaries/control-medication';


    constructor(private router: Router, private userService: UserService, private chartFilter: ChartFilterService) {
        router.events.subscribe((event) => {
 
            // TP#170094 If user navigates to the Controlled Medication Summary page the filters are defaulted to Both. 
            // The drilldowns also maintain thier state when navigating from the parent Controlled Medication 
            // Summary page.
            if (event instanceof NavigationStart) {
            
                this.onNavigationStart(event);

                let newPath: string = event.url;
 
                if (this.oldPath.length !== newPath.length && newPath === this.path && !this.oldPath.startsWith(this.path)) {
                   chartFilter.setDefaultFilterStateBoth();
                }
 
                this.oldPath = event.url;
            }
        });
    }

    onNavigationStart(event) {

        let changePasswordUrl = '/changePassword';
        let isOnChangePassword = event.url.includes(changePasswordUrl);
        if (this.userService.needPasswordChange && !this.userService.isImpersonating 
            && !isOnChangePassword && !this.userService.isSSOLogin) {
            
            let redirect = event.url;
            this.router.navigate([changePasswordUrl],{ queryParams: { redirectUrl: redirect, canNavigateAway:false } });
        }
    }
}
